import {
  angleFromValue,
  valueFromAngle,
  type RotaryKnobConfig,
} from "../lib/rotaryKnobMath";
import { LearnableControl } from "./LearnableControl";
import { RotaryKnob } from "./RotaryKnob";

export type ParamKnobProps = {
  /** Omit or pass empty to hide the caption (keep `ariaLabel` for a11y). */
  label?: string;
  min: number;
  max: number;
  step: number;
  value: number;
  defaultValue?: number;
  format?: (value: number) => string;
  size?: number;
  density?: "default" | "compact";
  /** `below` = stacked caption/value; `end` = value to the right of the dial. */
  valuePlacement?: "below" | "end";
  config?: RotaryKnobConfig;
  ariaLabel?: string;
  onChange: (value: number) => void;
  /** When set, right-click Learn MIDI/OSC; amber corner mark when bound. */
  learnPath?: string;
};

/** Labeled rotary control: React RotaryKnob + value readout. */
export function ParamKnob({
  label,
  min,
  max,
  step,
  value,
  defaultValue,
  format,
  size,
  density,
  valuePlacement = "below",
  config,
  ariaLabel,
  onChange,
  learnPath,
}: ParamKnobProps) {
  const fmt = format ?? ((v: number) => formatNumber(v, step));
  const clamped = snap(clamp(value, min, max), min, step);
  const resetTo = defaultValue ?? value;
  const range = Math.max(max - min, step);
  const stepNormalized = clamp(step / range, 1e-12, 1);
  const angle = angleFromValue(clamped, min, max, config);
  const caption = label?.trim() ?? "";
  const a11yLabel = ariaLabel ?? (caption || "Parameter");
  const valueEl = (
    <span
      className={
        valuePlacement === "end"
          ? "min-w-[2rem] text-left text-[10px] text-slate-300 tabular-nums"
          : "min-h-[1em] text-[10px] text-lab-muted tabular-nums"
      }
    >
      {fmt(clamped)}
    </span>
  );

  const body = (
    <div
      className={
        valuePlacement === "end"
          ? "flex items-center gap-[0.2rem]"
          : "flex min-w-[3.4rem] flex-col items-center gap-[0.28rem]"
      }
    >
      {caption ? (
        <span className="text-center text-[10px] leading-tight tracking-[0.04em] text-slate-500 uppercase">
          {caption}
        </span>
      ) : null}
      <RotaryKnob
        angle={angle}
        stepNormalized={stepNormalized}
        density={density}
        size={size}
        config={config}
        ariaLabel={a11yLabel}
        ariaValueMin={min}
        ariaValueMax={max}
        ariaValueNow={clamped}
        ariaValueText={fmt(clamped)}
        onAngleChange={nextAngle => {
          const next = valueFromAngle(nextAngle, min, max, step, config);
          if (next !== clamped) {
            onChange(next);
          }
        }}
        onReset={() => onChange(snap(clamp(resetTo, min, max), min, step))}
      />
      {valueEl}
    </div>
  );

  if (!learnPath) {
    return body;
  }
  return <LearnableControl path={learnPath}>{body}</LearnableControl>;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function snap(value: number, min: number, step: number): number {
  if (!(step > 0)) {
    return value;
  }
  return min + Math.round((value - min) / step) * step;
}

function formatNumber(value: number, step: number): string {
  if (step >= 1) {
    return String(Math.round(value));
  }
  if (step >= 0.1) {
    return value.toFixed(1);
  }
  if (step >= 0.01) {
    return value.toFixed(2);
  }
  return value.toFixed(3);
}
